import { MaritalStatus, maritalStatuses, UnitedStateCode } from "@goodfill/domain-customer-application-types";
import { capitalizeEveryFirstWordLetter } from "@goodfill/string";
import {
  Grid,
  InputDate,
  InputNumber,
  InputSelect,
  InputSelectOption,
  InputText,
  Span,
} from "@goodfill/ui-components";
import { DateTime } from "luxon";
import { Controller, useFormContext } from "react-hook-form";
import { CognitoIdToken } from "@goodfill/backend-api-client";
import { uploadDocumentForApplicationForm } from "../../../../../backendApi/lambdas/individualApplication/uploadDocumentForApplicationForm";
import { CountryInput } from "../../../../../../countries/CountryInput";
import { StateInput } from "../../../../../../countries/StateInput";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { FileUpload } from "../../../fileUpload/FileUpload";
import { FileUploadGroup } from "../../../fileUpload/FileUploadGroup";
import {
  AddressWithoutCountryScheme,
  defaultAddressWithoutCountryScheme,
} from "../../../../../../form/zod/formAddress";
import { CustomerApplicationFormState } from "../../../formState/formState";
import { FormYesOrNoQuestion } from "../../FormYesOrNoQuestion";
import "./Step2Personal.scss";

const today = () => DateTime.now();

const martialStatusOptions: InputSelectOption[] = maritalStatuses.map((value) => ({
  value,
  label: capitalizeEveryFirstWordLetter(value.replace("-", " ")),
}));

interface CustomerApplicationFormPersonalProps {
  cognitoIdToken: CognitoIdToken;
  areAllDisabled: boolean;
}

export const Step2Personal = ({ cognitoIdToken, areAllDisabled }: CustomerApplicationFormPersonalProps) => {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors },
  } = useFormContext<CustomerApplicationFormState>();

  const watchMailingAddress = watch("step2.sameMailingAddress", false);

  return (
    <div className="flex flex-col px-4 md:px-8 py-4 md:py-8 border-b border-neutral-110 items-center">
      <Span size="2xl" uppercase={false} font="semibold">
        Personal information
      </Span>

      <Span className="Steps_welcomeMessage" wrap={true}>
        To open a free account and start you investing safely, we need to know more about you. Let's get started!
      </Span>

      <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Date of Birth"
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputDate
                value={field.value ?? ""}
                setValue={field.onChange}
                border={error === undefined ? "neutral" : "red"}
                max={today().toFormat("yyyy-MM-dd")}
                onBlur={field.onBlur}
                ref={field.ref}
                // It currently covers the error message so its turned off
                openPickerOnFocus={false}
                id={"dateOfBirth"}
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step2.dateOfBirth"
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Marital Status"
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputSelect
                placeholder="Select Marital Status"
                options={martialStatusOptions}
                border={error === undefined ? "neutral" : "red"}
                selected={field.value}
                onBlur={field.onBlur}
                onChange={(value) => field.onChange(value === "" ? null : (value as MaritalStatus))}
                ref={field.ref}
                id="maritalStatus"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step2.maritalStatus"
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Number of Dependents"
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputNumber
                value={field.value}
                border={error === undefined ? "neutral" : "red"}
                setValue={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                id={"numberOfDependents"}
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step2.numberOfDependents"
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label={"Address"}
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputText
                value={field.value ?? ""}
                setValue={field.onChange}
                border={error === undefined ? "neutral" : "red"}
                placeholder="Street 1"
                onBlur={field.onBlur}
                ref={field.ref}
                id="addressStreet1"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name={"step2.address.street1"}
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label={"Address 2"}
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputText
                value={field.value ?? ""}
                setValue={field.onChange}
                border={error === undefined ? "neutral" : "red"}
                placeholder="Street 2"
                onBlur={field.onBlur}
                ref={field.ref}
                id="addressStreet2"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name={"step2.address.street2"}
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label={"City"}
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputText
                value={field.value ?? ""}
                setValue={field.onChange}
                border={error === undefined ? "neutral" : "red"}
                placeholder="City"
                onBlur={field.onBlur}
                ref={field.ref}
                id={"addressCity"}
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name={"step2.address.city"}
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label={"State"}
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <StateInput
                selected={field.value as UnitedStateCode}
                onChange={field.onChange}
                isValid={error === undefined}
                onBlur={field.onBlur}
                ref={field.ref}
                id="addressState"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name={"step2.address.state"}
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label={"Zip Code"}
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputText
                value={field.value ?? ""}
                setValue={field.onChange}
                border={error === undefined ? "neutral" : "red"}
                placeholder="Zip"
                onBlur={field.onBlur}
                ref={field.ref}
                id={"addressZipCode"}
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name={"step2.address.zipCode"}
          control={control}
        />
      </Grid>
      <Grid className="w-full">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id="sameMailingAddress"
              label={"Is your residence address the same as your mailing address?"}
              value={field.value}
              onChange={(value) => {
                setValue(
                  "step2.mailingAddress",
                  // Necessary cast to hack react-hook-from absence of support for the "non-valid" state
                  value ? null : (defaultAddressWithoutCountryScheme as AddressWithoutCountryScheme),
                );
                field.onChange(value);
              }}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-full-important"
            />
          )}
          name={"step2.sameMailingAddress"}
          control={control}
        />
      </Grid>

      <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
        {!watchMailingAddress && (
          <>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={"Address"}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    value={field.value ?? ""}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    placeholder="Street 1"
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={"mailingAddressStreet1"}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name={"step2.mailingAddress.street1"}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={"Address 2"}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    value={field.value ?? ""}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    placeholder="Street 2"
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={"mailingAddressStreet2"}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name={"step2.mailingAddress.street2"}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={"City"}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    value={field.value ?? ""}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    placeholder="City"
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={"mailingAddressCity"}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name={"step2.mailingAddress.city"}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={"State"}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <StateInput
                    selected={field.value as UnitedStateCode}
                    onChange={field.onChange}
                    isValid={error === undefined}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id="mailingAddressState"
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name={"step2.mailingAddress.state"}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={"Zip Code"}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    value={field.value ?? ""}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    placeholder="Zip"
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id="mailingAddressZipCode"
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name={"step2.mailingAddress.zipCode"}
              control={control}
            />
          </>
        )}
      </Grid>
      <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <CountryInput
              title="Issuing Country of Identification"
              selected={field.value}
              onChange={field.onChange}
              validation={error}
              onBlur={field.onBlur}
              ref={field.ref}
              id={"issuingCountryOfIdentification"}
            />
          )}
          name={"step2.issuingCountryOfIdentification"}
          control={control}
        />
      </Grid>
      <Grid cols={2} gap={0.75} className="w-full max-w-2xl input-file-select">
        <FileUploadGroup
          validation={
            [
              formErrors.step2?.proofOfIdentityFileFrontId?.message,
              formErrors.step2?.proofOfIdentityFileBackId?.message,
            ].filter((a) => a !== undefined) as string[]
          }
          title={
            <div className="flex flex-col">
              <span>Proof of Identity</span>
              <Span neutral size="sm">
                (Govt. Issued ID, DL or Passport)
              </Span>
            </div>
          }
          fileUploads={[
            <Controller
              key={"proofOfIdentityFileFrontId"}
              render={({ field }) => (
                <FileUpload
                  id="proofOfIdentityFileFrontId"
                  fileName="ProofOfIdentity"
                  onUpload={async (file) => {
                    const fileId = await uploadDocumentForApplicationForm("ProofOfIdentity", file, cognitoIdToken);
                    field.onChange(fileId);
                    trigger("step2.proofOfIdentityFileFrontId");
                  }}
                  onReset={() => {
                    field.onChange("");
                    trigger("step2.proofOfIdentityFileFrontId");
                  }}
                  text={"Upload Front Side"}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
              name={"step2.proofOfIdentityFileFrontId"}
              control={control}
            />,
            <Controller
              key={"proofOfIdentityFileBackId"}
              render={({ field }) => (
                <FileUpload
                  id="proofOfIdentityFileBackId"
                  fileName="ProofOfIdentity"
                  onUpload={async (file) => {
                    const fileId = await uploadDocumentForApplicationForm("ProofOfIdentity", file, cognitoIdToken);
                    field.onChange(fileId);
                    trigger("step2.proofOfIdentityFileBackId");
                  }}
                  onReset={() => {
                    field.onChange("");
                    trigger("step2.proofOfIdentityFileBackId");
                  }}
                  text={"Upload Back Side"}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
              name={"step2.proofOfIdentityFileBackId"}
              control={control}
            />,
          ]}
        />

        <FileUploadGroup
          validation={[formErrors.step2?.proofOfAddressFileId?.message ?? ""]}
          title={
            <div className="flex flex-col">
              <span>Upload proof of Address</span>
              <Span neutral size="sm">
                &nbsp;
              </Span>
            </div>
          }
          fileUploads={[
            <Controller
              key="proofOfAddressFileId"
              render={({ field }) => (
                <FileUpload
                  id="proofOfAddressFileId"
                  fileName="ProofOfAddress"
                  onUpload={async (file) => {
                    const fileId = await uploadDocumentForApplicationForm("ProofOfAddress", file, cognitoIdToken);
                    field.onChange(fileId);
                    trigger("step2.proofOfAddressFileId");
                  }}
                  onReset={() => {
                    field.onChange("");
                    trigger("step2.proofOfAddressFileId");
                  }}
                  width={80}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
              name={"step2.proofOfAddressFileId"}
              control={control}
            />,
          ]}
        />
      </Grid>
    </div>
  );
};
